import { selectors } from 'modules/common';
import { stateTree } from 'modules/data';
import React, { FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { OffboardUserPopupData } from '../../reducers/page/activePopupReducer';
import { getActivePopup } from '../../selectors/pageSelectors';
import { RequestReplacementMode } from './OffboardUserPopupContent.types';

export interface IOffboardingContext {
    setShowErrors: (value: boolean) => void;
    showErrors: boolean;
    requestReplacementMode: RequestReplacementMode;
    setRequestReplacementMode: (value: RequestReplacementMode) => void;
    teamWithoutOnboarded: selectors.types.ExpandedCompanyUser[];
    selectedReplacements: { [key: string]: selectors.types.ExpandedUser };
    selectReplacement: (id: string, value: selectors.types.ExpandedUser | null) => void;
}

const OffboardingContext = React.createContext<IOffboardingContext>(null as any as IOffboardingContext);

export const OffboardingProvider: FC<PropsWithChildren> = (props) => {
    const { children } = props;

    const teamWithoutOnboarded = useSelector((state: stateTree.State) => {
        const companyId = selectors.navigation.getActiveCompanyId(state)!;
        const company = selectors.company.getCompanyById(state, companyId);
        const team = selectors.company.getCompanyTeam(state, company);
        const offboardingUser = getActivePopup<OffboardUserPopupData>(state).userEmail;

        if (company.isReadonly) {
            return team.filter((user) => user.id === company.author);
        }

        return team.filter((user) => user.id !== offboardingUser);
    });

    const [selectedReplacements, setSelectedReplacements] = useState<{
        [key: string]: selectors.types.ExpandedUser;
    }>({});

    const selectReplacement = useCallback(
        (id: string, value: selectors.types.ExpandedUser) => {
            const newData = { ...selectedReplacements, [id]: value };

            if (!newData[id]) {
                delete newData[id];
            }

            setSelectedReplacements(newData);
        },
        [selectedReplacements]
    );

    const company = useSelector(selectors.navigation.getActiveCompany);

    const [requestReplacementMode, setRequestReplacementMode] = useState(
        company.flags.isWorkflowManager ? RequestReplacementMode.OneForAll : RequestReplacementMode.Each
    );
    const [showErrors, setShowErrors] = useState(false);

    const value = useMemo(
        () => ({
            teamWithoutOnboarded,
            showErrors,
            setShowErrors,
            selectedReplacements,
            selectReplacement,
            requestReplacementMode,
            setRequestReplacementMode,
        }),
        [teamWithoutOnboarded, showErrors, selectedReplacements, selectReplacement, requestReplacementMode]
    );

    return <OffboardingContext.Provider value={value}>{children}</OffboardingContext.Provider>;
};

export const useOffboardingContext = () => {
    return useContext(OffboardingContext);
};
